// lista dokumentów (local – opcja Pobierz z KSEF gdy ksef_qr1/ksef_qr2)
// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { reduxActions, useDispatch } from 'store';
import { useGetDocuments, useGetDocumentsSummary } from 'api';
import type { IDocumentListItem, IRequest as IDocumentsRequest } from 'api/endpoints/useGetDocuments';
import { DropDown, PageTitle } from 'components/controls';
import { PaymentsOptions } from 'components/containers';
import Table, { IColumn } from 'components/controls/Table';
import ExportButton from 'pages/Documents/components';

import { FiletypePdf, QrCode, ThreeDotsVertical } from 'react-bootstrap-icons';
import styles from 'theme/pages/Documents/Documents.module.scss';

const Documents = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tabs: {
    type: IDocumentsRequest['document_type'];
    name: string;
    key: 'proforma' | 'invoices' | 'corrections';
  }[] = [
    { type: 'PROFORMA', name: t('Proforma'), key: 'proforma' },
    { type: 'INVOICE', name: t('Faktury'), key: 'invoices' },
    { type: 'CORRECTIVE_INVOICE', name: t('Korekty'), key: 'corrections' }
  ];

  const [queryParams, setQueryParams] = useState<IDocumentsRequest>({
    page: 1,
    limit: 20,
    document_type: 'PROFORMA'
  });

  const { data: documentsSummaryData } = useGetDocumentsSummary();
  const { data: documentsData } = useGetDocuments(queryParams);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Moje dokumenty') }
      ])
    );
  }, []);

  const isKsefValueEmpty = (v: string | null | undefined): boolean =>
    v == null || String(v).trim() === '';

  /** Mapowanie ksef_status z API (success / error / false) na etykiety PL/EN */
  const getKsefStatusLabel = (status: string | null | undefined): string => {
    if (isKsefValueEmpty(status)) return '–';
    const s = String(status).trim().toLowerCase();
    if (s === 'success') return t('KSeF status – success');
    if (s === 'error') return t('KSeF status – error');
    if (s === 'false') return t('KSeF status – false');
    return status ?? '–';
  };

  const hasKsefLink = (doc: IDocumentListItem) =>
    !isKsefValueEmpty(doc.ksef_qr1) || !isKsefValueEmpty(doc.ksef_qr2);

  const getKsefUrl = (doc: IDocumentListItem): string | null => {
    const q1 = !isKsefValueEmpty(doc.ksef_qr1) ? String(doc.ksef_qr1).trim() : null;
    const q2 = !isKsefValueEmpty(doc.ksef_qr2) ? String(doc.ksef_qr2).trim() : null;
    return q1 ?? q2;
  };

  const actionMenuItems = (item: IDocumentListItem) => [
    ...(hasKsefLink(item)
      ? [
          {
            label: (
              <div
                className={styles.actionMenuItemLabel}
                onClick={() => {
                  const url = getKsefUrl(item);
                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                }}>
                <QrCode /> {t('Pobierz z KSEF')}
              </div>
            )
          }
        ]
      : []),
    ...(item.has_document_files && !hasKsefLink(item)
      ? [
          {
            label: (
              <ExportButton id={item.id} fileType="pdf">
                <FiletypePdf /> {t('Pobierz PDF')}
              </ExportButton>
            )
          }
        ]
      : []),
    ...(item.status_payment_formatted.show_button_pay
      ? [
          {
            label: (
              <PaymentsOptions
                paymentStatus={item.status_payment_formatted}
                id={item.id}
                provider="DOCUMENT"
              />
            )
          }
        ]
      : [])
  ];

  const columns: IColumn<IDocumentListItem>[] = useMemo(
    () => [
      {
        title: <Trans>LP</Trans>,
        key: 'lp',
        align: 'center',
        renderCell: (item, index) => index + 1,
        width: 50
      },
      {
        title: t('Nr faktury'),
        dataIndex: 'symbol',
        align: 'left',
        renderCell: (item) => <span className={styles.light}>{item.symbol}</span>
      },
      {
        title: t('Symbol KSeF'),
        dataIndex: 'ksef_symbol',
        align: 'left',
        renderCell: (item) => (
          <span className={styles.light}>{item.ksef_symbol ?? '–'}</span>
        )
      },
      {
        title: t('Status KSeF'),
        dataIndex: 'ksef_status',
        align: 'left',
        renderCell: (item) => (
          <span className={styles.light}>{getKsefStatusLabel(item.ksef_status)}</span>
        )
      },
      {
        title: <Trans>Data płatności</Trans>,
        dataIndex: 'payment_datetime_formatted',
        align: 'right',
        renderCell: (item) => (
          <span className={styles.light}>{item.payment_datetime_formatted}</span>
        )
      },
      {
        title: <Trans>Data utworzenia</Trans>,
        dataIndex: 'create_datetime_formatted',
        align: 'right',
        renderCell: (item) => (
          <span className={styles.light}>{item.create_datetime_formatted}</span>
        )
      },
      {
        title: <Trans>Wartość brutto</Trans>,
        dataIndex: 'value_gross',
        align: 'right',
        renderCell: (item) => (
          <span className={styles.light}>
            {item.value_gross_formatted} {item.currency}
          </span>
        )
      },
      {
        title: <Trans>Opcje</Trans>,
        align: 'center',
        renderCell: (item) =>
          (item.has_document_files ||
            item.status_payment_formatted.show_button_pay ||
            hasKsefLink(item)) && (
            <div className={styles.actionsCell}>
              <DropDown
                label={<ThreeDotsVertical />}
                items={actionMenuItems(item)}
                disableClose
                withDropdownIcon={false}
              />
            </div>
          )
      }
    ],
    []
  );

  const renderMobileItem = (item: IDocumentListItem) => (
    <div key={item.id} className={styles.mobileListItem}>
      <div className={styles.itemHeader}>{item.symbol}</div>
      <div className={styles.itemBody}>
        <div>
          <span className={styles.label}>
            <Trans>Data utworzenia</Trans>
          </span>
          {item.create_datetime_formatted}
        </div>
        <div>
          <span className={styles.label}>
            <Trans>Data płatności</Trans>
          </span>
          {item.payment_datetime_formatted}
        </div>
        <div>
          <span className={styles.label}>
            <Trans>Wartość brutto</Trans>
          </span>
          <strong>
            {item.value_gross_formatted} {item.currency}
          </strong>
        </div>
      </div>
    </div>
  );

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Documents')}>
      <PageTitle
        title={
          <>
            <Trans>Moje dokumenty</Trans>{' '}
            <span className="thin">({documentsData?.total_count})</span>
          </>
        }
      />

      <div className={styles.summary}>
        {Object.entries(documentsSummaryData || {}).map(([key, item]) => (
          <div
            className={classnames(styles.summaryBox, {
              [styles.active]:
                queryParams.document_type === tabs.find((tab) => tab.key === key)?.type
            })}
            key={key}
            onClick={() =>
              setQueryParams((prevState) => ({
                ...prevState,
                document_type: tabs.find((tab) => tab.key === key)?.type || 'PROFORMA',
                page: 1
              }))
            }>
            <div className={styles.summaryName}>
              <img className={styles.icon} src={item.icon} alt="icon" />
              {item.name}
            </div>
            <div className={styles.summaryCounter}>{item.value}</div>
          </div>
        ))}
      </div>

      <div className={styles.tabsHeader}>
        {tabs.map((item) => (
          <div
            key={item.type}
            className={classnames(styles.tabsBox, {
              [styles.active]: queryParams.document_type === item.type
            })}
            onClick={() =>
              setQueryParams((prevState) => ({ ...prevState, document_type: item.type, page: 1 }))
            }>
            <div className={styles.summaryName}>{item.name}</div>
          </div>
        ))}
      </div>

      <div className={styles.tableWrapper}>
        <Table<IDocumentListItem>
          columns={columns}
          dataSource={documentsData?.items || []}
          rowKey="id"
          mobileItem={renderMobileItem}
          pagination={{
            page: queryParams.page || 1,
            pagesCount: documentsData?.total_pages || 1,
            onChange: (page) => setQueryParams((prevState) => ({ ...prevState, page }))
          }}
        />
      </div>
    </div>
  );
};

export default Documents;
